/**
 * Initial Setup Tab Component
 * Allows super admin to pre-fill basic information for new users
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Building, 
  Mail, 
  Phone, 
  MapPin,
  Save,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { SuperAdminOnboardingData } from '@/types/super-admin-onboarding';
import { OnboardingStatus } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface InitialSetupTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const InitialSetupTab: React.FC<InitialSetupTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState({
    restaurant_name: data.restaurant_name || '',
    organisation_name: data.organisation_name || '',
    address: data.address || '',
    email: data.email || data.user_email || '',
    phone: data.phone || '',
    contact_person: data.contact_person || data.user_name || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update form data when the data prop changes
  useEffect(() => {
    setFormData({
      restaurant_name: data.restaurant_name || '',
      organisation_name: data.organisation_name || '',
      address: data.address || '',
      email: data.email || data.user_email || '',
      phone: data.phone || '',
      contact_person: data.contact_person || data.user_name || ''
    });
    setHasChanges(false);
  }, [data]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(formData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const changedFields = Object.entries(formData).filter(([field, value]) => {
          const originalValue = data[field as keyof SuperAdminOnboardingData];
          return originalValue !== value;
        });
        
        if (changedFields.length === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
          return;
        }
        
        const updates = changedFields.map(([field, value]) => 
          onFieldUpdate(field as keyof SuperAdminOnboardingData, value)
        );
        
        await Promise.all(updates);
        
        toast({
          title: "Success",
          description: `Updated ${changedFields.length} field(s) successfully`
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save initial setup information",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Auto-populate from user data
  const handleAutopopulate = () => {
    setFormData(prev => ({
      ...prev,
      email: data.user_email,
      contact_person: data.user_name,
      organisation_name: data.organisation_name || prev.organisation_name
    }));
    setHasChanges(true);
    
    toast({
      title: "Information populated",
      description: "Basic information has been populated from user profile"
    });
  };

  // Mark as ready for customer
  const handleMarkReady = async () => {
    if (!formData.restaurant_name || !formData.email) {
      toast({
        title: "Required fields missing",
        description: "Restaurant name and email are required before marking as ready",
        variant: "destructive"
      });
      return;
    }

    try {
      // Save current changes first
      if (hasChanges) {
        await handleSave();
      }
      
      // Update business details status to awaiting confirmation
      await onFieldUpdate('business_details_status', OnboardingStatus.AWAITING_CONFIRMATION);
      
      toast({
        title: "Success",
        description: "Initial setup marked as ready for customer review"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  // Check completion status
  const isComplete = data.business_details_status === OnboardingStatus.COMPLETE;
  const isAwaitingConfirmation = data.business_details_status === OnboardingStatus.AWAITING_CONFIRMATION;
  const hasRequiredFields = formData.restaurant_name && formData.email;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Initial Setup</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Pre-fill basic information to help the customer get started
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isComplete && (
                <Badge variant="default" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Complete
                </Badge>
              )}
              {isAwaitingConfirmation && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Ready for Customer
                </Badge>
              )}
              {!isComplete && !isAwaitingConfirmation && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Needs Setup
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleAutopopulate}>
              <User className="h-4 w-4 mr-2" />
              Populate from User Profile
            </Button>
            <Button 
              variant="outline" 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Use the populate button to auto-fill information from the user's account
          </p>
        </CardContent>
      </Card>

      {/* Basic Information Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="h-5 w-5" />
            Business Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="restaurant_name">
                Restaurant Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="restaurant_name"
                value={formData.restaurant_name}
                onChange={(e) => handleFieldChange('restaurant_name', e.target.value)}
                placeholder="Enter restaurant name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="organisation_name">Organisation Name</Label>
              <Input
                id="organisation_name"
                value={formData.organisation_name}
                onChange={(e) => handleFieldChange('organisation_name', e.target.value)}
                placeholder="Enter organisation name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">
              <MapPin className="h-4 w-4 inline mr-1" />
              Business Address
            </Label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder="Enter full business address"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contact Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => handleFieldChange('contact_person', e.target.value)}
                placeholder="Primary contact name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="h-4 w-4 inline mr-1" />
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="contact@restaurant.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">
              <Phone className="h-4 w-4 inline mr-1" />
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              placeholder="+64 21 123 4567"
            />
          </div>
        </CardContent>
      </Card>

      {/* Status Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Status Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Next Steps</h4>
            {!hasRequiredFields ? (
              <p className="text-sm text-muted-foreground">
                Fill in the required fields (Restaurant Name and Email) to proceed.
              </p>
            ) : !isAwaitingConfirmation && !isComplete ? (
              <p className="text-sm text-muted-foreground">
                Once you've filled in the basic information, mark it as ready for the customer to review and complete.
              </p>
            ) : isAwaitingConfirmation ? (
              <p className="text-sm text-muted-foreground">
                This information is ready for the customer to review. They can now access and complete their business details.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                This section has been completed by the customer.
              </p>
            )}
          </div>
          
          <div className="flex gap-2">
            {hasChanges && (
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
            
            {!isComplete && hasRequiredFields && (
              <Button 
                variant="default" 
                onClick={handleMarkReady}
                disabled={hasChanges}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Ready for Customer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InitialSetupTab;